import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Image as ImageIcon,
  Heart,
  Upload,
  Search,
  X,
  Trash2,
  Calendar,
  Tag,
  Sparkles,
  Shuffle,
  Loader2,
  Star,
} from 'lucide-react'
import { useAgentStore } from '../store/useAgentStore'
import { fetcher, apiClient } from '../api/client'

interface Memory {
  id: number
  title: string
  content: string | null
  memory_date: string | null
  tags: string[] | null
  emotion_tag: string | null
  importance: number
  media_urls: string[] | null
  is_favorite: boolean
  created_at: string
}

interface UploadFormData {
  title: string
  content: string
  memory_date: string
  tags: string
  emotion_tag: string
  importance: number
  files: FileList | null
}

export default function MemoryGallery() {
  const { t } = useTranslation()
  const { selectedElderlyId, language } = useAgentStore()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [semanticSearch, setSemanticSearch] = useState(false)
  const [selectedMemory, setSelectedMemory] = useState<Memory | null>(null)

  const [uploadForm, setUploadForm] = useState<UploadFormData>({
    title: '',
    content: '',
    memory_date: '',
    tags: '',
    emotion_tag: '',
    importance: 3,
    files: null,
  })

  const isRTL = language === 'ar' || language === 'he' || language === 'fa'

  /* ───────── Queries ───────── */
  const {
    data: memories,
    isLoading,
    refetch,
  } = useQuery<Memory[]>({
    queryKey: ['memories', selectedElderlyId],
    queryFn: () => fetcher<Memory[]>(`/memory/${selectedElderlyId}`),
    enabled: !!selectedElderlyId,
  })

  const { refetch: fetchRandom } = useQuery<Memory>({
    queryKey: ['random-memory', selectedElderlyId],
    queryFn: () => fetcher<Memory>(`/memory/${selectedElderlyId}/random`),
    enabled: false,
  })

  /* ───────── Mutations ───────── */
  const toggleFavorite = useMutation({
    mutationFn: (memoryId: number) =>
      apiClient.patch<Memory>(`/memory/${memoryId}`, {
        is_favorite: true,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['memories'] }),
  })

  const deleteMemory = useMutation({
    mutationFn: (memoryId: number) =>
      apiClient.delete(`/memory/${memoryId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories'] })
      setSelectedMemory(null)
    },
  })

  const uploadMemory = useMutation({
    mutationFn: async (formData: FormData) => {
      const response = await apiClient.post<Memory>(
        `/memory/upload/${selectedElderlyId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memories'] })
      setShowUpload(false)
      resetUploadForm()
    },
  })

  const searchMemories = useMutation({
    mutationFn: async (query: string) => {
      const response = await apiClient.get<Memory[]>(
        `/memory/${selectedElderlyId}/search`,
        {
          params: { q: query, semantic: semanticSearch },
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      // For simplicity, we just refetch the main list after search
      // In a real app you might store search results separately
      queryClient.setQueryData(['memories', selectedElderlyId], data)
    },
  })

  /* ───────── Helpers ───────── */
  const parseMediaUrls = useCallback((urls: string[] | null): string[] => {
    // API returns a list already — tolerate legacy string payloads too
    if (!urls) return []
    if (Array.isArray(urls)) return urls
    try {
      const parsed = JSON.parse(urls as unknown as string)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [])

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleDateString(language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const resetUploadForm = () => {
    setUploadForm({
      title: '',
      content: '',
      memory_date: '',
      tags: '',
      emotion_tag: '',
      importance: 3,
      files: null,
    })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleUploadSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!uploadForm.title.trim() || !selectedElderlyId) return

    const formData = new FormData()
    formData.append('title', uploadForm.title)
    if (uploadForm.content) formData.append('content', uploadForm.content)
    if (uploadForm.memory_date) formData.append('memory_date', uploadForm.memory_date)
    if (uploadForm.tags) formData.append('tags', uploadForm.tags)
    if (uploadForm.emotion_tag) formData.append('emotion_tag', uploadForm.emotion_tag)
    formData.append('importance', String(uploadForm.importance))

    if (uploadForm.files) {
      Array.from(uploadForm.files).forEach((file) => {
        formData.append('files', file)
      })
    }

    uploadMemory.mutate(formData)
  }

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      refetch()
      return
    }
    searchMemories.mutate(searchQuery)
  }

  const handleRandomMemory = () => {
    fetchRandom().then((result) => {
      if (result.data) setSelectedMemory(result.data)
    })
  }

  /* ───────── Render Guards ───────── */
  if (!selectedElderlyId) {
    return (
      <div className="text-center py-20 text-gray-500 dark:text-gray-400 text-xl">
        {t('memories.no_elderly_selected')}
      </div>
    )
  }

  const filteredMemories = memories || []

  /* ───────── Component ───────── */
  return (
    <div className="space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('memories.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            {t('memories.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="btn-primary flex items-center justify-center gap-2"
        >
          <Upload size={20} />
          {t('memories.upload')}
        </button>
      </div>

      {/* Search & Controls */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search
            size={20}
            className="absolute start-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t('memories.search_placeholder')}
            className="input-field ps-12"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('')
                refetch()
              }}
              className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          className="btn-primary px-4 py-3"
          disabled={searchMemories.isPending}
        >
          {searchMemories.isPending ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            t('memories.search')
          )}
        </button>
        <button
          onClick={() => setSemanticSearch((v) => !v)}
          className={`px-4 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2 ${
            semanticSearch
              ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
          }`}
          title={t('memories.semantic_search_tooltip')}
        >
          <Sparkles size={18} />
          {t('memories.semantic')}
        </button>
        <button
          onClick={handleRandomMemory}
          className="px-4 py-3 rounded-xl font-semibold bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
        >
          <Shuffle size={18} />
          {t('memories.random')}
        </button>
      </div>

      {/* Memories Grid */}
      {isLoading ? (
        <div className="text-center py-12 text-xl text-gray-500 dark:text-gray-400">
          <Loader2 size={32} className="animate-spin mx-auto mb-4" />
          {t('common.loading')}
        </div>
      ) : filteredMemories.length === 0 ? (
        <div className="card text-center py-16">
          <ImageIcon size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('memories.empty_title')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">{t('memories.empty_desc')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMemories.map((memory) => {
            const media = parseMediaUrls(memory.media_urls)

            return (
              <div
                key={memory.id}
                className="card group hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer"
                onClick={() => setSelectedMemory(memory)}
              >
                {/* Media Preview */}
                <div className="relative h-48 -mx-6 -mt-6 mb-4 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                  {media.length > 0 ? (
                    <img
                      src={media[0]}
                      alt={memory.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon size={48} />
                    </div>
                  )}
                  {/* Favorite Badge */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleFavorite.mutate(memory.id)
                    }}
                    className={`absolute top-3 end-3 p-2 rounded-full shadow-md transition-colors ${
                      memory.is_favorite
                        ? 'bg-danger-500 text-white'
                        : 'bg-white/90 dark:bg-gray-800/90 text-gray-400 hover:text-danger-500'
                    }`}
                  >
                    <Heart size={18} fill={memory.is_favorite ? 'currentColor' : 'none'} />
                  </button>
                  {/* Importance Badge */}
                  {memory.importance >= 4 && (
                    <div className="absolute top-3 start-3 bg-warning-500 text-white text-xs font-bold px-2 py-1 rounded-lg shadow-md flex items-center gap-1">
                      <Star size={12} fill="currentColor" />
                      {t('memories.important')}
                    </div>
                  )}
                  {/* Multiple Media Indicator */}
                  {media.length > 1 && (
                    <div className="absolute bottom-3 end-3 bg-black/60 text-white text-xs px-2 py-1 rounded-lg">
                      +{media.length - 1}
                    </div>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {memory.title}
                </h3>
                {memory.memory_date && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <Calendar size={14} />
                    {formatDate(memory.memory_date)}
                  </div>
                )}
                {memory.content && (
                  <p className="text-gray-600 dark:text-gray-300 line-clamp-3 mb-3">
                    {memory.content}
                  </p>
                )}
                {memory.tags && memory.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {memory.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 text-xs bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300 px-2 py-1 rounded-lg"
                      >
                        <Tag size={12} />
                        {tag.trim()}
                      </span>
                    ))}
                  </div>
                )}
                {memory.emotion_tag && (
                  <div className="inline-flex items-center gap-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-lg">
                    {t(`emotions.${memory.emotion_tag}`) || memory.emotion_tag}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('memories.upload_title')}
              </h2>
              <button
                onClick={() => {
                  setShowUpload(false)
                  resetUploadForm()
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleUploadSubmit} className="space-y-5">
              <div>
                <label className="label">{t('memories.form_title')}</label>
                <input
                  type="text"
                  required
                  value={uploadForm.title}
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, title: e.target.value }))
                  }
                  className="input-field"
                  placeholder={t('memories.form_title_placeholder')}
                />
              </div>

              <div>
                <label className="label">{t('memories.form_content')}</label>
                <textarea
                  rows={4}
                  value={uploadForm.content}
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, content: e.target.value }))
                  }
                  className="input-field resize-none"
                  placeholder={t('memories.form_content_placeholder')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('memories.form_date')}</label>
                  <input
                    type="date"
                    value={uploadForm.memory_date}
                    onChange={(e) =>
                      setUploadForm((f) => ({ ...f, memory_date: e.target.value }))
                    }
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="label">{t('memories.form_emotion')}</label>
                  <select
                    value={uploadForm.emotion_tag}
                    onChange={(e) =>
                      setUploadForm((f) => ({ ...f, emotion_tag: e.target.value }))
                    }
                    className="input-field"
                  >
                    <option value="">{t('memories.form_emotion_none')}</option>
                    <option value="happy">{t('emotions.happy')}</option>
                    <option value="sad">{t('emotions.sad')}</option>
                    <option value="nostalgic">{t('emotions.nostalgic')}</option>
                    <option value="proud">{t('emotions.proud')}</option>
                    <option value="loved">{t('emotions.loved')}</option>
                    <option value="peaceful">{t('emotions.peaceful')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="label">{t('memories.form_tags')}</label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) =>
                    setUploadForm((f) => ({ ...f, tags: e.target.value }))
                  }
                  className="input-field"
                  placeholder={t('memories.form_tags_placeholder')}
                />
              </div>

              <div>
                <label className="label">
                  {t('memories.form_importance')}: {uploadForm.importance}
                </label>
                <input
                  type="range"
                  min={1}
                  max={5}
                  value={uploadForm.importance}
                  onChange={(e) =>
                    setUploadForm((f) => ({
                      ...f,
                      importance: Number(e.target.value),
                    }))
                  }
                  className="w-full accent-primary-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>{t('memories.importance_low')}</span>
                  <span>{t('memories.importance_high')}</span>
                </div>
              </div>

              <div>
                <label className="label">{t('memories.form_photos')}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) =>
                    setUploadForm((f) => ({
                      ...f,
                      files: e.target.files,
                    }))
                  }
                  className="input-field py-2"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {t('memories.form_photos_hint')}
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={uploadMemory.isPending || !uploadForm.title.trim()}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {uploadMemory.isPending ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Upload size={20} />
                  )}
                  {t('memories.form_submit')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowUpload(false)
                    resetUploadForm()
                  }}
                  className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-semibold"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Memory Detail Modal */}
      {selectedMemory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedMemory.title}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (confirm(t('memories.delete_confirm'))) {
                      deleteMemory.mutate(selectedMemory.id)
                    }
                  }}
                  className="p-2 rounded-lg hover:bg-danger-50 text-danger-600 dark:hover:bg-danger-900/30"
                  title={t('memories.delete')}
                >
                  <Trash2 size={20} />
                </button>
                <button
                  onClick={() => setSelectedMemory(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Media Gallery */}
            {(() => {
              const media = parseMediaUrls(selectedMemory.media_urls)
              return media.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {media.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`${selectedMemory.title} ${idx + 1}`}
                      className="w-full h-64 object-cover rounded-xl"
                    />
                  ))}
                </div>
              ) : null
            })()}

            <div className="space-y-3">
              {selectedMemory.memory_date && (
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Calendar size={16} />
                  {formatDate(selectedMemory.memory_date)}
                </div>
              )}
              {selectedMemory.content && (
                <p className="text-lg text-gray-700 dark:text-gray-200 leading-relaxed">
                  {selectedMemory.content}
                </p>
              )}
              {selectedMemory.tags && selectedMemory.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {selectedMemory.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 text-sm bg-primary-50 text-primary-700 dark:bg-primary-900 dark:text-primary-300 px-3 py-1 rounded-lg"
                    >
                      <Tag size={14} />
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              )}
              {selectedMemory.emotion_tag && (
                <div className="pt-2">
                  <span className="inline-flex items-center gap-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-3 py-1 rounded-lg">
                    {t(`emotions.${selectedMemory.emotion_tag}`) || selectedMemory.emotion_tag}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}